<template>
  <component
    :is="as"
    :class="computedClass"
    v-bind="$attrs"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mouseup="onMouseUp"
    @mouseover="onMouseOver"
  >
    <slot />
  </component>
</template>

<script lang="ts">
import { PropType } from 'vue'
import { mapMutations, mapState } from 'vuex'

import { ChecklistDroppableAreaVariant, ChecklistSection } from '@/project/checklist/types'
import { SET_CHECKLIST_DRAG_OVER_SECTION } from '@/store/modules/checklist/mutation_types'

export default {
  name: 'ChecklistTableDroppableArea',
  emits: ['mouseenter', 'mouseleave', 'mouseUpDrop'],
  props: {
    as: {
      type: [String, Object],
      default: 'div',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String as PropType<ChecklistDroppableAreaVariant>,
      default: '',
    },
    section: {
      type: Object as PropType<ChecklistSection>,
      default: null,
    },
  },
  data() {
    return {
      isActive: false,
    }
  },
  computed: {
    ...mapState('checklist', ['checklistDragItem']),
    computedClass() {
      return {
        [this.variant]: !!this.variant, // 'after-section', 'after-subsection', 'after-task'
        'dragging-over': this.checklistDragItem,
        'drop-zone-active': this.isActive,
        'drop-disabled': this.disabled && this.checklistDragItem,
      }
    },
  },
  watch: {
    checklistDragItem(newVal) {
      if (!newVal) {
        this.isActive = false
      }
    },
  },
  methods: {
    ...mapMutations('checklist', [SET_CHECKLIST_DRAG_OVER_SECTION]),

    onMouseEnter(event: MouseEvent) {
      if (this.section) {
        this.SET_CHECKLIST_DRAG_OVER_SECTION(this.section)
      } else {
        this.SET_CHECKLIST_DRAG_OVER_SECTION(null)
      }
      event.stopPropagation()
      event.preventDefault()

      this.$emit('mouseenter')

      if (this.checklistDragItem) {
        this.isActive = true
      }
    },
    onMouseOver() {
      if (this.checklistDragItem) {
        this.isActive = true
      }
    },
    onMouseLeave() {
      if (this.section) {
        this.SET_CHECKLIST_DRAG_OVER_SECTION(null)
      }

      this.$emit('mouseleave')
      this.isActive = false
    },
    onMouseUp() {
      if (this.disabled) return

      if (this.checklistDragItem && this.isActive) {
        this.$emit('mouseUpDrop')
      }

      this.isActive = false
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/elevation-tokens";

.after-subsection {
  position: relative;
  @include elevation('z-index-sticky-header');
  height: 0px;
  &.dragging-over {
    height: 8px;
    transition: height 0.2s ease-in-out;
  }
}

.after-task {
  @include elevation('z-index-sticky-header');
  height: 4px;
  grid-column: 1 / -1;
  margin-top: -4px;
}

.drop-zone-active {
  transition: all 0.1s ease-in-out;
  background-color: rgb(var(--v-theme-surface-info)) !important;
  cursor: grabbing;

  & * {
    cursor: grabbing;
    pointer-events: none !important;
  }

  &.after-section {
    @include border-radius('border-radius-3');
    outline: 1px solid rgb(var(--v-theme-border-info-bold));
    outline-offset: -1px;
  }

  &.after-subsection {
    border: get-border('border-info-bold');
    @include border-radius('border-radius-2');
  }

  &.after-task {
    background-color: rgb(var(--v-theme-surface-info-bold)) !important;
  }
}

.drop-disabled {
  cursor: not-allowed !important;
  outline: none !important;
  color: rgb(var(--v-theme-text-disabled)) !important;
  background-color: rgb(var(--v-theme-surface-secondary)) !important;
}
</style>
